/**
 * useVerifyInstanceStatus - Hook para verificação de status real de instâncias
 * 
 * Chama a Edge Function provider-proxy com action 'get_connection_status'
 * para obter o status real diretamente do provider (WAHA, Z-API, etc).
 * 
 * @version 1.0.0
 * @author Sistema
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { showToast } from "@/lib/toast";

/**
 * Interface para resultado da verificação de status
 */
export interface VerifyStatusResult {
  success: boolean;
  error?: string;
  // Dados retornados do provider
  data?: {
    connected: boolean;
    status: 'active' | 'disconnected' | 'connecting' | 'error';
    wahaStatus?: string; // Status bruto do WAHA (WORKING, STOPPED, etc)
    reason?: string; // Motivo (session_not_found, etc)
    me?: {
      id?: string;
      pushName?: string;
    } | null;
    engine?: string | { 
      grpc?: { client?: string; stream?: string }; 
      gows?: { found?: boolean; connected?: boolean } 
    }; // Engine WAHA - pode ser string (NOWEB, WEBJS) ou objeto (GOWS)
  };
  // Metadados da verificação
  localStatus?: string; // Status atual no banco
  mismatch?: boolean; // true se status local != status real
  verifiedAt?: string; // Timestamp da verificação
  sessionName?: string; // Nome da sessão no provider
}

/**
 * Hook para verificar status real de instâncias de providers
 * 
 * Utiliza a Edge Function provider-proxy existente com action 'get_connection_status'
 * para consultar o status diretamente no servidor do provider (WAHA, Z-API, etc).
 */
export function useVerifyInstanceStatus() {
  const queryClient = useQueryClient();

  /**
   * Mutation para verificar status de uma única instância
   */
  const verifyStatus = useMutation({
    mutationFn: async ({ 
      inboxId, 
      localStatus 
    }: { 
      inboxId: string; 
      localStatus?: string;
    }): Promise<VerifyStatusResult> => {
      logger.info('useVerifyInstanceStatus', 'Iniciando verificação de status real', { inboxId });
      
      const verifiedAt = new Date().toISOString();
      
      try {
        // Chama provider-proxy com action get_connection_status
        const { data, error } = await supabase.functions.invoke('provider-proxy', {
          body: { 
            inbox_id: inboxId, 
            action: 'get_connection_status' 
          }
        });
        
        if (error) {
          logger.error('useVerifyInstanceStatus', 'Erro ao chamar provider-proxy', error);
          return {
            success: false,
            error: error.message || 'Erro ao verificar status',
            localStatus,
            verifiedAt
          };
        }
        
        // Verifica se há divergência entre status local e real
        const realStatus = data?.data?.status || 'disconnected';
        const mismatch = localStatus ? localStatus !== realStatus : false;
        
        if (mismatch) {
          logger.warn('useVerifyInstanceStatus', 'Divergência detectada entre status local e real', {
            inboxId,
            localStatus,
            realStatus,
            wahaStatus: data?.data?.wahaStatus
          });
        }
        
        logger.info('useVerifyInstanceStatus', 'Verificação concluída', {
          inboxId,
          connected: data?.data?.connected,
          realStatus,
          wahaStatus: data?.data?.wahaStatus,
          mismatch
        });
        
        return {
          success: true,
          data: data?.data,
          localStatus,
          mismatch,
          verifiedAt
        };
      } catch (err) {
        logger.error('useVerifyInstanceStatus', 'Exceção ao verificar status', err);
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro desconhecido',
          localStatus,
          verifiedAt
        };
      }
    },
    onSuccess: (result) => {
      // Invalida cache para atualizar tabela se status foi alterado
      if (result.mismatch) {
        queryClient.invalidateQueries({ queryKey: ['provider-instances-admin'] });
      }
    },
    onError: (error) => {
      logger.error('useVerifyInstanceStatus', 'Erro na mutation', error);
      showToast.error('Erro ao verificar status da instância');
    }
  });

  /**
   * Mutation para corrigir status local para corresponder ao real
   */
  const correctStatus = useMutation({
    mutationFn: async ({ 
      inboxId, 
      newStatus 
    }: { 
      inboxId: string; 
      newStatus: 'active' | 'disconnected' | 'connecting' | 'error' | 'inactive';
    }) => {
      logger.info('useVerifyInstanceStatus', 'Corrigindo status local', { inboxId, newStatus });
      
      const { error } = await supabase
        .from('inboxes')
        .update({ status: newStatus })
        .eq('id', inboxId);
        
      if (error) {
        logger.error('useVerifyInstanceStatus', 'Erro ao corrigir status', error);
        throw error;
      }
      
      // Atualiza last_validated_at nas credenciais
      await supabase
        .from('inbox_credentials')
        .update({ last_validated_at: new Date().toISOString() })
        .eq('inbox_id', inboxId);
      
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-instances-admin'] });
      showToast.success('Status corrigido com sucesso');
    },
    onError: (error) => {
      logger.error('useVerifyInstanceStatus', 'Erro ao corrigir status', error);
      showToast.error('Erro ao corrigir status');
    }
  });

  return {
    verifyStatus,
    correctStatus,
    isVerifying: verifyStatus.isPending,
    isCorrecting: correctStatus.isPending
  };
}
